import { Hidden, Link } from '@mui/material'
import cn from 'classnames'
import Image from 'next/image'
import React, { Dispatch, useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { BetaTag } from 'src/components/global/BetaTag/BetaTag'
import CompareButton from 'src/components/global/CompareButton'
import { PortfolioPickerOption } from 'src/components/global/CreateNewPortfolioModal'
import { EnhancedTag } from 'src/components/global/EnhancedTag/EnhancedTag'
import { FollowButton } from 'src/components/global/FollowButton'
import { HtmlContentViewer } from 'src/components/global/HtmlContentViewer/HtmlContentViewer'
import { PremiumFollowActionButton } from 'src/components/global/PremiumFollowActionButton'
import { PremiumFollowNote } from 'src/components/global/PremiumFollowNote'
import { PremiumTag } from 'src/components/global/PremiumTag/PremiumTag'
// import { ReviewWithLinkItem } from 'src/components/global/ReviewWithLinkItem'
import { SignUpModal } from 'src/components/global/SignUpModal'
import { StartupTag } from 'src/components/global/StartupTag/StartupTag'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { VerifyEmailModal } from 'src/components/global/VerifyEmailModal'
import { THEME } from 'src/config/config'
import {
  IEnum_Solution_Item_Type,
  ISolutionListItemFragment,
  ISubTopicItemFragment,
  ITopicItemFragment,
  useFollowSharedPortfolioSolutionMutation,
  useFollowSolutionMutation,
} from 'src/graphql/generated/hooks'
import { useGetMyCustomTagsQuery } from 'src/graphql/manual/portfolioCustomTagHooks'
import { useAuthenticated } from 'src/hooks/useAuthenticated'
import { showToast } from 'src/store/actions/toastAction'
import { convertSolutionItemTypeToSolutionType } from 'src/utils/convertSolutionItemTypeToSolutionType'
import { removeTags } from 'src/utils/removeTags'
import { isEmailInSharedPortfolioMembers } from 'src/utils/sharedPortfolioMembership'

import { AddCustomTagModal } from '../../CustomTag/AddCustomTagModal'
import { CustomTagItem, CustomTagList } from '../../CustomTag/CustomTagList'
import { LinkWithOrigin } from '../../LinkWithOrigin/LinkWithOrigin'
import CategoryList from './CategoryList'
import VendorLogo from './VendorLogo'

export interface VendorListItemProps {
  item: ISolutionListItemFragment
  href: string
  topic?: ITopicItemFragment
  subTopic?: ISubTopicItemFragment
  companyName?: string
  showCompare?: boolean
  showFollow?: boolean
  onRefresh?: (item: boolean) => void
  isFrom?: string
  isManage?: boolean
  editSolutionHref?: string
  itemSlug?: string
  followOnly?: boolean
  refreshFollowVendorList?: () => void | Promise<void>
  portfolioSlug?: string
  portfolioPickerOptions?: PortfolioPickerOption[]
  onSubmitSharedPortfolio?: () => void | Promise<void>
  sharedPortfolioId?: string
  solutionId?: string | null
  /** Portfolio custom tags UI (list + add) — only used on my-followings. */
  showPortfolioCustomTags?: boolean
  refetchSideBarTagsData?: () => void
}

export const VendorListItem: React.FC<VendorListItemProps> = ({
  item,
  href,
  topic,
  subTopic,
  companyName,
  showCompare = true,
  showFollow = true,
  onRefresh,
  isFrom = '',
  isManage = false,
  editSolutionHref = '',
  itemSlug = '',
  followOnly = false,
  refreshFollowVendorList,
  portfolioSlug = '',
  portfolioPickerOptions = [],
  onSubmitSharedPortfolio,
  sharedPortfolioId = undefined,
  // solutionId = null,
  showPortfolioCustomTags = false,
  refetchSideBarTagsData = () => {},
}) => {
  const content = removeTags(item.description || '')
  const isPremium = !!item.isPremium
  const isEnhanced = isPremium || !!item.isEnhanced
  const isStartup = !!item.isStartup
  const isBeta = !!item.isBeta
  const subTopics = item.subTopics?.filter((subTopic): subTopic is string => !!subTopic) || []
  const [showSignUp, setShowSignUp] = useState(false)
  const [showEmailVerify, setShowEmailVerify] = useState(false)
  const solutionType = convertSolutionItemTypeToSolutionType(item.type)
  const isCompanyListing = solutionType === null
  const displayName = item.displayName
  const [showAddNote, setShowAddNote] = useState(false)
  const [followSolution] = useFollowSolutionMutation()
  const [followSharedPortfolioSolution] = useFollowSharedPortfolioSolutionMutation()
  const [editMode, setEditMode] = useState(false)
  const [showAddCustomTag, setShowAddCustomTag] = useState(false)
  const { session } = useAuthenticated()
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { data: myTagsData, refetch: loadMyTags } = useGetMyCustomTagsQuery({
    variables: {
      portfolioId: sharedPortfolioId,
      isSharedPortfolio: isFrom === 'my-private-portfolio' ? false : true,
    },
    skip: !showPortfolioCustomTags || !session?.user,
    fetchPolicy: 'network-only',
  })

  const solutionDocumentId = item.documentId || ''
  const solutionIdNum = 0
  const tagsFromItem = useMemo(() => (item?.customTagsWithName as CustomTagItem[]) || [], [item.customTagsWithName])
  const tagIdsKey = useMemo(() => tagsFromItem.map((t) => t.id).join(','), [tagsFromItem])
  const [displayTags, setDisplayTags] = useState<CustomTagItem[]>(tagsFromItem)

  useEffect(() => {
    setDisplayTags(tagsFromItem)
  }, [item.documentId, tagIdsKey, tagsFromItem])
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const dispatch: Dispatch<any> = useDispatch()

  const [followingType, setFollowingType] = useState<string>()
  const [localFollowingSolutionNote, setLocalFollowingSolutionNote] = useState<string>(
    item?.followedSolutionsData?.followingSolutionNote || '',
  )
  const [shared_portfolio_solutions, setShared_portfolio_solutions] = useState<any[]>([])

  const email = session?.user.email || ''
  const isUserPremium = session?.user.isPremium
  const [itemFollowState, setItemFollowState] = useState<boolean>(false)
  const handleCloseSignUp = () => {
    setShowSignUp(false)
  }
  const handleCloseEmailVerify = () => {
    setShowEmailVerify(false)
  }

  const handleShowAddNote = () => {
    setShowAddNote(true)
  }

  const handleCloseAddNote = () => {
    setShowAddNote(false)
  }

  const handleFollow = async (type: string | null, updateType: 'Move' | 'AddNote' | 'Remove' | 'RemoveNote') => {
    const solutionId = item?.documentId || ''
    const itemFollowType = followingType || item?.followedSolutionsData?.followingType || ''

    const createFollowSolutionVariables = () => {
      switch (updateType) {
        case 'Move':
          return {
            id: solutionId || '',
            follow: true,
            followType: type || null,
          }
        case 'Remove':
          return {
            id: solutionId || '',
            follow: false,
          }
        case 'AddNote': {
          const oldNote = item?.followedSolutionsData?.followingSolutionNote || ''
          const newNote = type || ''
          if (oldNote === newNote) return null
          return {
            id: solutionId || '',
            follow: true,
            followingSolutionNote: newNote,
          }
        }
        case 'RemoveNote':
          return {
            id: solutionId || '',
            follow: true,
            followingSolutionNote: '',
          }
        default:
          return null
      }
    }

    const createFollowSharedPortfolioSolutionVariables = () => {
      switch (updateType) {
        case 'Move':
          return {
            portfolioSlug: portfolioSlug ?? '',
            solutionId: item.documentId || '',
            follow: true,
            categoryType: type || null,
            updateType: 'Move',
          }
        case 'Remove':
          return {
            portfolioSlug: portfolioSlug ?? '',
            solutionId: item.documentId || '',
            follow: false,
            updateType: 'Remove',
          }
        case 'AddNote': {
          const newNote = type || ''
          const oldNote = localFollowingSolutionNote || item?.followedSolutionsData?.followingSolutionNote || ''
          if (oldNote === newNote) return null
          return {
            portfolioSlug: portfolioSlug ?? '',
            solutionId: item.documentId || '',
            follow: true,
            followingSolutionNote: newNote,
            categoryType: followingType || item?.followedSolutionsData?.followingType || 'Portfolio',
            updateType: 'AddNote',
          }
        }
        case 'RemoveNote':
          return {
            portfolioSlug: portfolioSlug ?? '',
            solutionId: item.documentId || '',
            follow: true,
            followingSolutionNote: '',
            updateType: 'RemoveNote',
          }
        default:
          return null
      }
    }

    try {
      const isSharedPortfolio = isFrom === 'my-shared-portfolio' || portfolioSlug?.trim() !== ''
      let mutationSucceeded = false
      let followState = true

      if (isSharedPortfolio) {
        const sharedPortfolioVariables = createFollowSharedPortfolioSolutionVariables()
        if (!sharedPortfolioVariables) return
        followState = sharedPortfolioVariables.follow
        const { data } = await followSharedPortfolioSolution({ variables: sharedPortfolioVariables })
        mutationSucceeded = !!data?.followSharedPortfolioSolution
      } else {
        const followVariables = createFollowSolutionVariables()
        if (!followVariables) return
        followState = followVariables.follow
        const { data } = await followSolution({ variables: followVariables })
        mutationSucceeded = !!data?.followSolution
      }

      if (mutationSucceeded) {
        if (updateType === 'Move' && type) {
          setFollowingType(type)
          // dispatch(showToast(`Added to your followed solutions in ${type}`, 'confirm_only_no_link'))
          dispatch(showToast(`Moved successfully.`, 'success'))
        } else if (updateType === 'AddNote') {
          dispatch(showToast('Note saved successfully.', 'success'))
        } else if (updateType === 'RemoveNote') {
          dispatch(showToast('Note deleted successfully.', 'success'))
        } else if (!followState) {
          const message = ['Portfolio', 'Reviewed', 'Watching'].includes(itemFollowType)
            ? `Removed from your Portfolio` //`Removed from your ${itemFollowType} solutions`
            : 'Removed from your followed solutions'
          dispatch(showToast(message, 'success'))
        }

        setTimeout(() => {
          refreshFollowVendorList?.()
          if (showPortfolioCustomTags) void loadMyTags()
        }, 1000)
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error handling follow action:', error)
      dispatch(showToast('An error occurred while processing your request. Please try again.', 'error'))
    }
  }

  const handleNoteAction = (note: string, updateType: 'AddNote' | 'RemoveNote') => {
    setLocalFollowingSolutionNote(updateType === 'RemoveNote' ? '' : note || '')
    handleFollow(note, updateType)
    handleCloseAddNote()
  }

  const refreshSolutionTags = useCallback(
    async (removedTagId?: string) => {
      if (removedTagId) {
        setDisplayTags((prev) => prev.filter((t) => t.id !== removedTagId))
      }

      const refetchList = async () => {
        await refreshFollowVendorList?.()
        void refetchSideBarTagsData?.()
      }

      // OpenSearch reindex after remove can lag; delay refetch so searchSolutions returns updated tags.
      if (removedTagId) {
        setTimeout(() => void refetchList(), 800)
      } else {
        await refetchList()
      }
    },
    [refreshFollowVendorList, refetchSideBarTagsData],
  )

  /* update followingType, by verify item.followedSolutionsData?.followingType */
  useEffect(() => {
    const sharedSolutionsRaw = item?.shared_portfolio_solutions as unknown
    const normalizedSharedSolutions = Array.isArray(sharedSolutionsRaw)
      ? sharedSolutionsRaw
      : sharedSolutionsRaw &&
          typeof sharedSolutionsRaw === 'object' &&
          'data' in sharedSolutionsRaw &&
          Array.isArray((sharedSolutionsRaw as { data?: unknown }).data)
        ? (sharedSolutionsRaw as { data: any[] }).data ?? []
        : []
    const memberSharedSolutions = normalizedSharedSolutions.filter((obj: { members?: string }) =>
      isEmailInSharedPortfolioMembers(obj.members, email),
    )

    if (item.followedSolutionsData?.followingType && memberSharedSolutions.length > 0 && isFrom === '') {
      setFollowingType('Following')
    } else if (item.followedSolutionsData?.followingType) {
      setFollowingType(item.followedSolutionsData.followingType)
    } else if (memberSharedSolutions.length > 0) {
      if (isFrom === '' && memberSharedSolutions.length > 1) {
        setFollowingType('Following')
      } else if (isFrom === 'my-shared-portfolio' || isFrom === '' || isFrom === 'my-following') {
        setFollowingType(memberSharedSolutions[0].category)
      }
    }

    const privatePortfolioData = item?.followedSolutionsData || null
    const portfolioListData: any[] = []
    if (privatePortfolioData) {
      portfolioListData.push({
        solutionId: item.documentId || '',
        id: item.documentId + '-private' || '',
        followingType: privatePortfolioData.followingType || 'Portfolio',
        followingSolutionNote: privatePortfolioData.followingSolutionNote,
        members: [],
        name: 'My Portfolio',
        organizationId: null,
        portfolioId: null,
        sharedPortfolio: null,
        slug: '',
      })
    }

    memberSharedSolutions
      .slice()
      .sort((a: { name?: string }, b: { name?: string }) => {
        const aIsPrivate = a.name === 'My Portfolio'
        const bIsPrivate = b.name === 'My Portfolio'
        if (aIsPrivate && !bIsPrivate) return -1
        if (!aIsPrivate && bIsPrivate) return 1
        return (a.name ?? '').localeCompare(b.name ?? '')
      })
      .forEach((obj: any) => {
        portfolioListData.push({
          solutionId: item.documentId || '',
          id: item.documentId + '-shared-' + String(obj.portfolioId || '') || '',
          followingType: obj.category,
          followingSolutionNote: obj.followingSolutionNote,
          members: obj.members,
          name: obj.name,
          organizationId: obj.organizationId,
          portfolioId: obj.portfolioId,
          sharedPortfolio: obj.sharedPortfolio,
          slug: obj.slug,
        })
      })
    setShared_portfolio_solutions(portfolioListData)
  }, [email, isFrom, item.followedSolutionsData?.followingType, item.documentId, item.shared_portfolio_solutions])

  useEffect(() => {
    setItemFollowState(item.follow)
  }, [item])

  useEffect(() => {
    setLocalFollowingSolutionNote(item?.followedSolutionsData?.followingSolutionNote || '')
  }, [item.documentId, item?.followedSolutionsData?.followingSolutionNote])

  return (
    <div
      className={cn(
        'flex flex-row items-center pt-[14px] pb-[8px]',
        item.type === IEnum_Solution_Item_Type.Company ? 'bg-clouds' : '',
      )}
    >
      <div className="flex flex-row w-full md:items-center directory-card">
        <LinkWithOrigin
          toUrl={href}
          topicName={topic?.name}
          subTopicName={subTopic?.name}
          companyName={companyName}
          title={displayName}
        >
          <VendorLogo
            className="bg-white rounded-[.5rem] drop-shadow-md md:min-w-[120px] md:w-[120px] md:h-[120px] w-[80px] h-[80px] flex items-center justify-center object-contain"
            src={item.logo}
          />
          <Hidden mdUp>
            <ThemeOnly theme="legaltech">
              {isPremium && <PremiumTag className="mt-[10px] w-[80px] !p-0" variant="yellow" />}
              {!isPremium && isEnhanced && <EnhancedTag className="mt-[10px] w-[80px] !p-0" />}
              {isStartup && <StartupTag />}
              {isBeta && <BetaTag />}
            </ThemeOnly>
            <ThemeOnly theme="ilta">
              {(isPremium || isEnhanced) && <EnhancedTag className="mt-[10px] w-[80px] !p-0" />}
            </ThemeOnly>
          </Hidden>
        </LinkWithOrigin>
        <div className="grow flex-1 ml-[15px] md:ml-[1.75rem]">
          <div className="flex md:flex-row flex-col w-full justify-between ">
            <div className="flex flex-col ">
              <div className="w-full flex">
                <div className="flex-1">
                  <div className="w-full flex md:flex-row flex-col justify-between">
                    <div className="w-full flex items-center mb-1 flex-wrap">
                      <LinkWithOrigin
                        toUrl={href}
                        className="cursor-pointer md:text-[1.125rem] text-[1.0rem] text-textdark font-secondary font-bold mb-1.5"
                        topicName={topic?.name}
                        subTopicName={subTopic?.name}
                        companyName={companyName}
                      >
                        {isCompanyListing ? (
                          <>
                            <span className="mr-[10px]">{displayName}</span>
                            {THEME === 'legaltech' ? <PremiumTag variant="company" className="" /> : ' - Company'}
                          </>
                        ) : (
                          <span className="mr-[10px]">{displayName}</span>
                        )}

                        <Hidden mdDown>
                          <ThemeOnly theme="legaltech">
                            {isPremium && <PremiumTag variant="yellow" />}
                            {!isPremium && isEnhanced && <EnhancedTag />}
                            {isStartup && <StartupTag />}
                            {isBeta && <BetaTag />}
                          </ThemeOnly>
                          <ThemeOnly theme="ilta">
                            {(isPremium || isEnhanced) && <EnhancedTag className="ml-[10px] inline-block" />}
                          </ThemeOnly>
                        </Hidden>
                      </LinkWithOrigin>
                      {/* {solutionType && SHOW_REVIEWS && (
                        <div className="w-full flex md:flex-row flex-col">
                          <div className="flex flex-row">
                            <ThemeOnly theme="legaltech">
                              <ReviewWithLinkItem
                                reviewCnt={item.reviewCnt || 0}
                                starCnt={item.rating || 0}
                                title={displayName}
                                solutionType={solutionType}
                                id={`${item.documentId || ''}`}
                                isEnhanced={isEnhanced}
                                href={href || ''}
                              />
                            </ThemeOnly>
                          </div>
                        </div>
                      )} */}
                    </div>
                    {solutionType && (
                      <ThemeOnly theme="legaltech">
                        <Hidden mdDown>
                          <div className="flex flex-row">
                            {showCompare && (
                              <CompareButton
                                className="items-start"
                                slug={item.slug || ''}
                                solutionType={solutionType}
                              />
                            )}

                            {showFollow && (
                              <FollowButton
                                key={`${item?.documentId}-shared-portfolio-followbutton`}
                                solutionType={solutionType}
                                id={item?.documentId || ''}
                                isButton={false}
                                displayName={displayName}
                                isEnhanced={isEnhanced}
                                follow={itemFollowState}
                                isFromVendorList={true}
                                onRefresh={onRefresh}
                                isFrom={isFrom}
                                followTypeName={followingType || ''}
                                dirctorySaredPortfolio={true}
                                portfolioPickerOptions={portfolioPickerOptions as PortfolioPickerOption[] | undefined}
                                onSubmitSharedPortfolio={onSubmitSharedPortfolio}
                                sharedPortfolioId={sharedPortfolioId || undefined}
                                sharedPortfolioSolutions={shared_portfolio_solutions as any[] | []}
                                refreshFollowVendorList={refreshFollowVendorList}
                                portfolioSlug={
                                  ['my-private-portfolio', 'my-shared-portfolio', 'custom-tag'].includes(isFrom)
                                    ? portfolioSlug
                                    : ''
                                }
                                styleParameters={'top-[27px]'}
                              />
                            )}
                            {isUserPremium && followOnly && (
                              <PremiumFollowActionButton
                                className="ml-[16px]"
                                followTypeName={followingType || ''}
                                note={localFollowingSolutionNote}
                                onShowAddNote={handleShowAddNote}
                                handleFollow={handleFollow}
                                handleNoteAction={handleNoteAction}
                                setEditMode={setEditMode}
                                isFrom={isFrom}
                                solutionId={item.documentId?.toString() || ''}
                                sharedPortfolioId={sharedPortfolioId || undefined}
                                showAddCustomTag={showPortfolioCustomTags}
                                onShowAddCustomTag={() => setShowAddCustomTag(true)}
                              />
                            )}
                          </div>
                        </Hidden>
                      </ThemeOnly>
                    )}
                  </div>
                  <HtmlContentViewer className="text-[0.815rem] md:pr-[2rem] showmore-text" html={content} />
                  {solutionType && (
                    <ThemeOnly theme="legaltech">
                      <Hidden mdUp>
                        <div className="flex items-center mt-[12px]">
                          <CompareButton
                            className="items-start h-[unset] w-[unset]"
                            slug={item.slug || ''}
                            solutionType={solutionType}
                          />
                          <FollowButton
                            key={`${item?.documentId}-shared-portfolio-followbutton`}
                            solutionType={solutionType}
                            id={item?.documentId || ''}
                            isButton={false}
                            displayName={displayName}
                            isEnhanced={isEnhanced}
                            follow={itemFollowState}
                            isFromVendorList={true}
                            onRefresh={onRefresh}
                            isFrom={isFrom}
                            followTypeName={followingType || ''}
                            dirctorySaredPortfolio={true}
                            portfolioPickerOptions={portfolioPickerOptions as PortfolioPickerOption[] | undefined}
                            onSubmitSharedPortfolio={onSubmitSharedPortfolio}
                            sharedPortfolioId={sharedPortfolioId || undefined}
                            sharedPortfolioSolutions={shared_portfolio_solutions as any[] | []}
                            refreshFollowVendorList={refreshFollowVendorList}
                            portfolioSlug={
                              ['my-private-portfolio', 'my-shared-portfolio', 'custom-tag'].includes(isFrom)
                                ? portfolioSlug
                                : ''
                            }
                            styleParameters={'top-[27px]'}
                          />
                          {isUserPremium && followOnly && (
                            <PremiumFollowActionButton
                              className="ml-[16px]"
                              followTypeName={followingType || ''}
                              note={localFollowingSolutionNote}
                              onShowAddNote={handleShowAddNote}
                              handleFollow={handleFollow}
                              handleNoteAction={handleNoteAction}
                              setEditMode={setEditMode}
                              isFrom={isFrom}
                              solutionId={item.documentId || ''}
                              sharedPortfolioId={sharedPortfolioId || undefined}
                              showAddCustomTag={showPortfolioCustomTags}
                              onShowAddCustomTag={() => setShowAddCustomTag(true)}
                            />
                          )}
                        </div>
                      </Hidden>
                    </ThemeOnly>
                  )}
                </div>
                <ThemeOnly theme="ilta">
                  {item.iltaSponsorshipLogo && (
                    <Image src={item.iltaSponsorshipLogo} width={200} height={200} alt="" className="h-[70px] w-auto" />
                  )}
                </ThemeOnly>
              </div>
              {showPortfolioCustomTags && displayTags.length > 0 ? (
                <div className="mb-[6px] flex flex-wrap items-center">
                  <CustomTagList
                    tags={displayTags}
                    solutionId={solutionIdNum}
                    solutionDocumentId={solutionDocumentId}
                    onRefresh={(removedTagId) => void refreshSolutionTags(removedTagId)}
                  />
                  <CategoryList mergeInline listCategory={subTopics} />
                </div>
              ) : (
                <CategoryList className="mb-[6px]" listCategory={subTopics} />
              )}
              {isUserPremium && followOnly && (
                <PremiumFollowNote
                  dataKey={`${item?.documentId}-follownote`}
                  className=""
                  note={localFollowingSolutionNote}
                  showAddNote={showAddNote}
                  handleNoteAction={handleNoteAction}
                  setEditMode={setEditMode}
                  editMode={editMode}
                  noteAuthorship={item?.followedSolutionsData ?? null}
                />
              )}
            </div>
            <div className="flex justify-start sm:justify-end">
              {isManage && (
                <div className="flex md:flex-row flex-col md:items-center items-start justify-start sm:justify-end gap-1">
                  <Link href={editSolutionHref + '?viewType=manage'}>
                    <button className="px-3 box-border md:h-[50px] h-full py-2 w-[130px] border-solid border-[1px] border-mist rounded-full bg-navy text-white items-center justify-center font-semibold">
                      View / Edit
                    </button>
                  </Link>
                  {itemSlug && (
                    <Link href={`/add-annoucements/${itemSlug}/`}>
                      <button className="px-3 box-border md:h-[50px] h-full py-2 w-[200px] border-solid border-[1px] border-mist rounded-full bg-navy text-white items-center justify-center font-semibold">
                        Add Announcement
                      </button>
                    </Link>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      {showPortfolioCustomTags && (
        <AddCustomTagModal
          isOpen={showAddCustomTag}
          solutionId={solutionIdNum}
          solutionDocumentId={solutionDocumentId}
          portfolioId={sharedPortfolioId || ''}
          onClose={() => setShowAddCustomTag(false)}
          onSuccess={() => {
            void loadMyTags()
            setTimeout(() => void refreshSolutionTags(), 800)
          }}
        />
      )}
      <SignUpModal isOpen={showSignUp} onClose={handleCloseSignUp} />
      <VerifyEmailModal isOpen={showEmailVerify} email={email} onClose={handleCloseEmailVerify} />
    </div>
  )
}
